import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageModal from './LanguageModal';
import { LANGUAGES } from '../config/languages';
import { copyPageLink, openInExternalBrowser } from '../utils/openInExternalBrowser';
import { isSocialEmbeddedBrowser } from '../utils/embeddedBrowser';
import ga from '../services/analytics';

const EmbeddedBrowserWarning: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [appKey, setAppKey] = useState<string | null>(null);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    const detection = isSocialEmbeddedBrowser();

    if (detection.isEmbedded) {
      setIsEmbedded(true);
      setAppKey(detection.appKey);
      ga.event('embedded_browser_detected', { app_key: detection.appKey ?? 'unknown' });
    }
  }, []);

  if (!isEmbedded) {
    return null;
  }

  const appName = appKey ? t(`embeddedBrowser.apps.${appKey}.name`, { defaultValue: '' }) : null;
  const currentLanguage = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0];

  const handleOpenInBrowser = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpening(true);
    ga.event('embedded_browser_open_external', { app_key: appKey ?? 'unknown' });
    const result = openInExternalBrowser(appKey);
    if (result === 'failed') {
      void handleCopyLink(e);
    }
    setTimeout(() => setOpening(false), 1500);
  };

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const ok = await copyPageLink();
    if (ok) {
      setLinkCopied(true);
      ga.event('embedded_browser_copy_link', { app_key: appKey ?? 'unknown' });
      setTimeout(() => setLinkCopied(false), 4000);
    }
  };

  return (
    <>
      {/* Sticky banner only — no full-page overlay (that blocked Sign in clicks). */}
      <div
        className="sticky top-0 z-[100] bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/30 dark:to-orange-900/30 border-b-4 border-yellow-400 dark:border-yellow-600 shadow-lg"
        role="status"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-0.5 text-2xl sm:text-3xl">
              {t('embeddedBrowser.emoji')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm sm:text-base text-yellow-900 dark:text-yellow-100 leading-relaxed font-medium">
                    <strong className="text-base sm:text-lg">{t('embeddedBrowser.title')}</strong>
                    <br />
                    {appName
                      ? t('embeddedBrowser.messageWithApp', { appName })
                      : t('embeddedBrowser.messageGeneric')}
                  </p>

                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={handleOpenInBrowser}
                      disabled={opening}
                      data-testid="embedded-open-in-browser"
                      className="btn-primary w-full sm:w-auto px-5 py-2.5 text-sm sm:text-base font-bold rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-70"
                    >
                      {opening ? t('embeddedBrowser.opening') : t('embeddedBrowser.openInBrowser')}
                    </button>
                    <button
                      type="button"
                      onClick={handleCopyLink}
                      data-testid="embedded-copy-link"
                      className="w-full sm:w-auto px-5 py-2.5 text-sm font-semibold rounded-xl border-2 border-yellow-600 dark:border-yellow-500 text-yellow-900 dark:text-yellow-100 bg-white/80 dark:bg-yellow-950/40 hover:bg-white transition-colors"
                    >
                      {linkCopied ? t('embeddedBrowser.linkCopied') : t('embeddedBrowser.copyLink')}
                    </button>
                  </div>

                  <p className="mt-3 text-xs sm:text-sm text-yellow-800 dark:text-yellow-200">
                    💡 <strong>{t('embeddedBrowser.howToOpen')}</strong> {t('embeddedBrowser.instructions')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setLanguageOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/50 hover:bg-yellow-200 dark:hover:bg-yellow-900/70 transition-colors border border-yellow-300 dark:border-yellow-700 flex-shrink-0"
                  title={t('language.chooseLanguage')}
                >
                  <span className="text-lg">{currentLanguage.flag}</span>
                  <span className="text-sm font-medium text-yellow-900 dark:text-yellow-100 hidden sm:inline">
                    {currentLanguage.code.toUpperCase()}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LanguageModal open={languageOpen} onClose={() => setLanguageOpen(false)} />
    </>
  );
};

export default EmbeddedBrowserWarning;
